import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { getContract, USDT_ADDRESS, CONTRACT_ADDRESS } from "../utils";
import Modal from "./Modal";

export default function JoinPool({
  wallet,
  levelInvestments = [16, 32, 64, 128, 256, 512, 1024, 2048],
  refPool
}) {
  const [allPools, setAllPools] = useState([]);
  const [unlockedLevel, setUnlockedLevel] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [localIds, setLocalIds] = useState([]);
  const [selectedLocalId, setSelectedLocalId] = useState("");
  const [requiredAmount, setRequiredAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contract, setContract] = useState(null);
  const [pendingGlobalId, setPendingGlobalId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: "", message: "" });
  const [refTarget, setRefTarget] = useState(null);

  const LEVELS = {
    0: { name: "Entry" },
    1: { name: "Stacker" },
    2: { name: "Scaler" },
    3: { name: "Flipper" },
    4: { name: "Holder" },
    5: { name: "Broker" },
    6: { name: "Tycoon" },
    7: { name: "Legend" }
  };

  useEffect(() => {
    if (refPool && typeof refPool === "object") {
      setRefTarget(refPool);
      setSelectedLevel(refPool.level.toString());
      setSelectedLocalId(refPool.localId.toString());
    }
  }, [refPool]);

  useEffect(() => {
    if (!wallet) return;
    (async () => {
      try {
        const c = getContract(wallet.signer);
        const lvl = await c.getUnlockedLevel(wallet.address);
        setUnlockedLevel(Number(lvl));
      } catch (e) {
        console.error("Error fetching unlocked level:", e);
      }
    })();
  }, [wallet]);

  useEffect(() => {
    if (!wallet) return;
    (async () => {
      try {
        const c = getContract(wallet.signer);
        const raw = await c.getAllPools();
        const available = raw
          .map((p, idx) => ({
            globalId: idx + 1,
            level: Number(p.level),
            localId: p.id.toString(),
            status: Number(p.status)
          }))
          .filter(
            p => p.status === 0 &&
              (unlockedLevel === null || p.level <= unlockedLevel)
          );
        setAllPools(available);
      } catch (e) {
        console.error("Failed to fetch pools:", e);
      }
    })();
  }, [wallet, unlockedLevel]);

  useEffect(() => {
    if (!selectedLevel) {
      setLocalIds([]);
      return;
    }
    const ids = allPools
      .filter(p => p.level.toString() === selectedLevel)
      .map(p => p.localId);
    setLocalIds(ids);
    if (refTarget && refTarget.level.toString() === selectedLevel) {
      // keep referral
    } else if (!ids.includes(selectedLocalId)) {
      setSelectedLocalId("");
    }
  }, [selectedLevel, allPools]);

  const showAlert = (title, message) => setAlertModal({ show: true, title, message });

  async function handleJoinPool() {
    try {
      setLoading(true);
      if (!selectedLocalId) throw new Error("Please choose a pool.");
      const pool = allPools.find(p => p.localId === selectedLocalId);
      if (!pool) throw new Error("Pool not found.");
      const { globalId, level } = pool;

      const amount = ethers.parseUnits(
        levelInvestments[level].toString(),
        18
      );
      setRequiredAmount(amount);

      const c = getContract(wallet.signer);
      setContract(c);
      setPendingGlobalId(globalId);

      const usdt = new ethers.Contract(
        USDT_ADDRESS,
        [
          "function allowance(address owner,address spender) view returns (uint256)",
          "function approve(address spender,uint256 amount) returns (bool)"
        ],
        wallet.signer
      );
      const allowance = await usdt.allowance(
        wallet.address,
        CONTRACT_ADDRESS
      );
      if (allowance < amount) {
        setShowModal(true);
        return;
      }

      const tx = await c.joinPool(globalId);
      await tx.wait();

      showAlert(
        "Success",
        `✅ Joined ${LEVELS[level].name} Pool #${selectedLocalId}`
      );
    } catch (err) {
      console.error(err);
      const msg = err?.reason || err?.message || "Failed to join pool.";
      showAlert("Error", `❌ ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleApproveAndContinue() {
    try {
      setLoading(true);
      const usdt = new ethers.Contract(
        USDT_ADDRESS,
        ["function approve(address spender,uint256 amount) public returns (bool)"],
        wallet.signer
      );
      const approveTx = await usdt.approve(CONTRACT_ADDRESS, requiredAmount);
      await approveTx.wait();
      setShowModal(false);

      const tx = await contract.joinPool(pendingGlobalId);
      await tx.wait();

      showAlert("Success", "✅ Joined pool after approval!");
    } catch (e) {
      console.error(e);
      const errorMsg = e?.reason || e?.error?.message || e?.data?.message || e?.message || "Approval or join failed.";
      showAlert("Error", `❌ ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }


  const levelOptions =
    unlockedLevel != null
      ? Object.entries(LEVELS).filter(([lvl]) => Number(lvl) <= unlockedLevel)
      : Object.entries(LEVELS);

  return (
    <div className="join-pool-form">
      <h3 className="join-pool">Join a Pool</h3>

      {refTarget && (
        <p className="join-pool">
          Confirm Referral: Level<strong> {LEVELS[refTarget.level].name}</strong> Pool ID #<strong>{refTarget.localId}</strong>
        </p>
      )}

      <div className="mb-4">
        <select
          className="w-full p-2 border rounded"
          value={selectedLevel}
          onChange={e => setSelectedLevel(e.target.value)}
          disabled={loading || unlockedLevel == null}
        >
          <option value="" disabled>
            -- Choose level --
          </option>
          {levelOptions.map(([lvl, info]) => (
            <option key={lvl} value={lvl}>
              {info.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <select
          className="w-full p-2 border rounded"
          value={selectedLocalId}
          onChange={e => setSelectedLocalId(e.target.value)}
          disabled={loading || localIds.length === 0}
        >
          <option value="" disabled>
            -- Choose pool --
          </option>
          {localIds.map(id => (
            <option key={id} value={id}>
              Pool {id}
            </option>
          ))}
        </select>
      </div>

      <button
        onClick={handleJoinPool}
        className={`btn-primary w-full py-2 ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
        disabled={loading}
      >
        {loading ? "Processing..." : "Join Pool"}
      </button>

      {/* Approval Modal */}
      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="p-4 text-center">
            <h3 className="text-lg font-bold mb-2">Approve USDT</h3>
            <p className="text-sm mb-4 whitespace-pre-wrap">
              Your current allowance is insufficient.<br />
              Do you want to approve <strong>{ethers.formatUnits(requiredAmount, 18)}</strong> USDT to proceed?
            </p>
            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={() => setShowModal(false)}
                className="btn-cancel px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveAndContinue}
                disabled={loading}
                className="btn-primary px-4 py-2"
              >
                {loading ? "Approving..." : "Approve & Join"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Alert Modal */}
      {alertModal.show && (
        <Modal
          isOpen={alertModal.show}
          onClose={() => setAlertModal({ show: false, title: "", message: "" })}
        >
          <div className="p-4 text-center">
            <h3 className="text-xl font-bold mb-3 text-gray-800 dark:text-gray-100">
              {alertModal.title}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-6">
              {alertModal.message}
            </p>
            <button
              className="btn-primary px-4 py-2"
              onClick={() => setAlertModal({ show: false, title: "", message: "" })}
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}